import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import bookingApi from '../../api/bookingApi';
import { getSession } from '../../api';

const { 
  FiX, FiCalendar, FiClock, FiUser, FiCheckCircle, 
  FiChevronRight, FiChevronLeft, FiInfo, FiShield, FiHeart,
  FiAlertTriangle, FiLoader,
} = FiIcons;

// Convert "09:00 AM" -> "09:00" and compute end time
function parseTimeSlot(slotLabel, durationMinutes = 60) {
  const [time, meridian] = slotLabel.split(' ');
  let [hours, minutes] = time.split(':').map(Number);
  if (meridian === 'PM' && hours !== 12) hours += 12;
  if (meridian === 'AM' && hours === 12) hours = 0;
  const start = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  const endDate = new Date(`2000-01-01T${start}:00`);
  endDate.setMinutes(endDate.getMinutes() + durationMinutes);
  const end = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;
  return { start, end };
}

const ResourceBookingModal = ({ isOpen, onClose, resourceType = "Support Professional" }) => {
  const [step, setStep] = useState(1);
  const [bookingData, setBookingData] = useState({
    resourceId: null,
    date: '',
    time: '',
    notes: ''
  });
  const [checkingConflict, setCheckingConflict] = useState(false);
  const [conflictError, setConflictError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const allResources = useMemo(() => [
    { id: 1, name: 'Dr. Sarah Jenkins', role: 'Grief Counselor', rating: 4.9, image: 'https://images.unsplash.com/photo-1559839734-2b71f1536783?auto=format&fit=crop&q=80&w=200', merchantId: 'merch_grief_001', listingId: 'list_grief_001', price: 150, durationMinutes: 60 },
    { id: 2, name: 'Marcus Thorne', role: 'Estate Attorney', rating: 4.8, image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80&w=200', merchantId: 'merch_legal_001', listingId: 'list_legal_001', price: 300, durationMinutes: 60 },
    { id: 3, name: 'Elena Rodriguez', role: 'Funeral Director', rating: 5.0, image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=200', merchantId: 'merch_funeral_001', listingId: 'list_funeral_001', price: 0, durationMinutes: 90 },
    { id: 4, name: 'Dr. Michael Vance', role: 'Grief Counselor', rating: 4.7, image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=200', merchantId: 'merch_grief_002', listingId: 'list_grief_002', price: 125, durationMinutes: 60 },
  ], []);

  const filteredResources = useMemo(() => {
    if (resourceType === "Support Professional") return allResources;
    return allResources.filter(r => r.role.toLowerCase().includes(resourceType.toLowerCase()));
  }, [resourceType]);

  const selectedProfessional = useMemo(() => 
    allResources.find(r => r.id === bookingData.resourceId) || null,
  [allResources, bookingData.resourceId]);

  const timeSlots = ['09:00 AM', '10:30 AM', '01:00 PM', '02:30 PM', '04:00 PM'];

  const checkConflictAndAdvance = useCallback(async () => {
    if (!selectedProfessional || !bookingData.date || !bookingData.time) return;
    setCheckingConflict(true);
    setConflictError('');

    const { start, end } = parseTimeSlot(bookingData.time, selectedProfessional.durationMinutes);
    const available = await bookingApi.isSlotAvailable(
      selectedProfessional.merchantId,
      bookingData.date,
      start,
      end
    );

    setCheckingConflict(false);

    if (!available) {
      setConflictError('This time slot is no longer available. Please select another time.');
      return;
    }
    setStep(3);
  }, [selectedProfessional, bookingData.date, bookingData.time]);

  const handleNext = () => {
    if (step === 2) {
      checkConflictAndAdvance();
    } else {
      setStep(s => s + 1);
    }
  };
  const handleBack = () => {
    setStep(s => s - 1);
    setConflictError('');
    setSubmitError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');

    const user = getSession();
    if (!user) {
      setSubmitError('You must be signed in to book an appointment.');
      setSubmitting(false);
      return;
    }

    if (!selectedProfessional) {
      setSubmitError('Please select a professional first.');
      setSubmitting(false);
      return;
    }

    const { start, end } = parseTimeSlot(bookingData.time, selectedProfessional.durationMinutes);

    const payload = {
      merchant_id: selectedProfessional.merchantId,
      listing_id: selectedProfessional.listingId,
      customer_id: user.id || user.sub || 'guest',
      customer_name: user.name || user.email || 'Guest',
      customer_email: user.email || '',
      listing_title: `${selectedProfessional.name} — ${selectedProfessional.role}`,
      start_time: `${bookingData.date}T${start}:00`,
      end_time: `${bookingData.date}T${end}:00`,
      price: selectedProfessional.price,
      notes: bookingData.notes || '',
      pricing_model: selectedProfessional.price > 0 ? 'paid' : 'free',
    };

    try {
      const result = await bookingApi.createBooking(payload);
      if (result?.success) {
        setStep(4); // Success state
      } else {
        setSubmitError(result?.error || 'Booking could not be saved. Please try again.');
      }
    } catch (err) {
      setSubmitError(err?.message || 'An unexpected error occurred.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-start sm:items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md overflow-y-auto">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-2xl overflow-hidden border border-brand-dark flex flex-col my-4 sm:my-8"
        style={{ maxHeight: '90vh' }}
      >
        {/* Header */}
        <div className="p-6 md:p-8 border-b border-brand-light flex justify-between items-center bg-brand-light/30 flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-accent-gold text-white rounded-xl flex items-center justify-center">
              <SafeIcon icon={resourceType.includes('Counselor') ? FiHeart : FiCalendar} className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xl font-serif text-accent-navy">Book {resourceType}</h3>
              <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">Step {step} of 3</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-brand-light rounded-full text-text-muted transition-colors flex-shrink-0">
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-grow overflow-y-auto p-6 md:p-8">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div 
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className="flex items-center gap-2 text-accent-navy mb-4">
                  <SafeIcon icon={FiUser} className="w-5 h-5" />
                  <h4 className="font-bold">Select a {resourceType} Professional</h4>
                </div>
                <div className="grid gap-4">
                  {filteredResources.map((res) => (
                    <button
                      key={res.id}
                      onClick={() => {
                        setBookingData({ ...bookingData, resourceId: res.id });
                        handleNext();
                      }}
                      className={`flex items-center gap-4 p-4 rounded-3xl border-2 transition-all text-left ${bookingData.resourceId === res.id ? 'border-accent-gold bg-brand-light' : 'border-brand-light hover:border-accent-gold/50 bg-white'}`}
                    >
                      <img src={res.image} alt={res.name} className="w-14 h-14 rounded-2xl object-cover shadow-sm" />
                      <div className="flex-grow">
                        <p className="font-bold text-accent-navy">{res.name}</p>
                        <p className="text-xs text-text-muted">{res.role}</p>
                        <div className="flex items-center gap-1 mt-1">
                          <span className="text-accent-gold text-xs">★★★★★</span>
                          <span className="text-[10px] text-text-muted font-bold">{res.rating}</span>
                        </div>
                      </div>
                      <SafeIcon icon={FiChevronRight} className="text-brand-dark" />
                    </button>
                  ))}
                  {filteredResources.length === 0 && (
                    <div className="text-center py-10 text-text-muted italic text-sm">
                      No professionals found for this category.
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div 
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
              >
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-accent-navy mb-4">Select Date</label>
                  <input 
                    type="date" 
                    className="w-full p-4 rounded-2xl border-2 border-brand-light focus:border-accent-gold outline-none transition-colors"
                    value={bookingData.date}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={(e) => {
                      setBookingData({ ...bookingData, date: e.target.value });
                      setConflictError('');
                    }}
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-accent-navy mb-4">Available Time Slots</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {timeSlots.map((slot) => (
                      <button
                        key={slot}
                        onClick={() => {
                          setBookingData({ ...bookingData, time: slot });
                          setConflictError('');
                        }}
                        className={`p-3 rounded-xl border-2 text-xs font-bold transition-all ${bookingData.time === slot ? 'bg-accent-navy text-white border-accent-navy' : 'border-brand-light hover:border-brand-dark'}`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                </div>

                {conflictError && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-2xl text-red-700"
                  >
                    <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-bold">{conflictError}</p>
                      <p className="text-[10px] text-red-600/70 mt-0.5">Please choose a different time.</p>
                    </div>
                  </motion.div>
                )}

                <div className="flex justify-between pt-4">
                  <button onClick={handleBack} className="flex items-center gap-2 text-[10px] font-black uppercase text-text-muted hover:text-accent-navy">
                    <SafeIcon icon={FiChevronLeft} /> Back
                  </button>
                  <button 
                    disabled={!bookingData.date || !bookingData.time || checkingConflict}
                    onClick={handleNext}
                    className="px-8 py-3 bg-accent-gold text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-accent-gold/20 disabled:opacity-50"
                  >
                    {checkingConflict ? 'Checking…' : 'Continue'}
                  </button>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.form 
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <div className="bg-brand-light/50 p-6 rounded-3xl border border-brand-dark space-y-4">
                  <div className="flex items-center gap-3 text-accent-navy">
                    <SafeIcon icon={FiInfo} className="w-5 h-5" />
                    <h4 className="font-bold">Booking Summary</h4>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <p className="text-text-muted uppercase font-black text-[9px] mb-1">Professional</p>
                      <p className="font-bold">{allResources.find(r => r.id === bookingData.resourceId)?.name}</p>
                    </div>
                    <div>
                      <p className="text-text-muted uppercase font-black text-[9px] mb-1">Schedule</p>
                      <p className="font-bold">{bookingData.date} at {bookingData.time}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-accent-navy mb-2">Additional Notes</label>
                  <textarea 
                    placeholder="Briefly describe what you'd like to discuss..."
                    className="w-full p-4 rounded-2xl border-2 border-brand-light focus:border-accent-gold outline-none h-32 resize-none"
                    value={bookingData.notes}
                    onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
                  />
                </div>

                <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-2xl text-blue-800">
                  <SafeIcon icon={FiShield} className="w-5 h-5 mt-0.5" />
                  <p className="text-[10px] leading-relaxed">
                    <b>Privacy Notice:</b> Your initial consultation request is encrypted. This professional has signed our Legacy Charter for confidential handling of estate matters.
                  </p>
                </div>

                {submitError && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-2xl text-red-700"
                  >
                    <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <p className="text-xs font-bold">{submitError}</p>
                  </motion.div>
                )}

                <div className="flex justify-between pt-4">
                  <button type="button" onClick={handleBack} className="flex items-center gap-2 text-[10px] font-black uppercase text-text-muted">
                    <SafeIcon icon={FiChevronLeft} /> Back
                  </button>
                  <button 
                    type="submit"
                    disabled={submitting}
                    className="px-10 py-4 bg-accent-navy text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-accent-navy/20"
                  >
                    {submitting ? 'Confirming…' : 'Confirm Booking'}
                  </button>
                </div>
              </motion.form>
            )}

            {step === 4 && (
              <motion.div 
                key="step4"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-12 space-y-6"
              >
                <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto">
                  <SafeIcon icon={FiCheckCircle} className="w-10 h-10" />
                </div>
                <div>
                  <h3 className="text-2xl font-serif text-accent-navy mb-2">Request Sent</h3>
                  <p className="text-sm text-text-muted max-w-sm mx-auto">
                    Your booking request has been sent to {allResources.find(r => r.id === bookingData.resourceId)?.name}. You will receive a confirmation email shortly.
                  </p>
                </div>
                <button 
                  onClick={onClose}
                  className="px-8 py-3 border-2 border-brand-dark rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-brand-light transition-colors"
                >
                  Close Window
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default ResourceBookingModal;